import { useEffect, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  enforceLessSecure,
  removeEventListeners,
  PROHIBITED_ACTION,
  incrementViolationCount,
  VIOLATION_COUNT_PLACEHOLDER,
  isFullscreen,
  switchToFullscreen,
  setFullscreen,
  isApiFullscreen,
  getConfigBasedOnPnp,
  getPriorityAction,
  isScreenSplit,
  getPwaConfig,
} from '../../scripts/less-secure/enforceLessSecure';
import { AssessmentContext } from '../../context/AssessmentContext';
import { KioskContext } from '../../context/KioskProviderContext.js';
import * as PwaEvents from '../../context/PwaEvents.js';
import {
  showCustomDialog,
  LEARNOSITY_EVENTS,
  hideCustomDialog,
} from '../../scripts/learnosityScripts.js';
import { PAUSED_DUE_TO_ACTION } from '../../components/assessment/LearnosityDialogs.js';
import useSessionStorage from '../../hooks/useSessionStorage.js';
import * as Storage from '../common/Storage.js';
import * as TestStatus from '../assessment/TestStatus.js';
import { LearnosityContainerObservable } from '../assessment/LearnosityContainer.js';
import { PWAEventsObservable } from '../assessment/WebsocketMessageHandler.js';
import { Observable } from '../../scripts/common/Observable.js';
import { hasNetworkErrors } from '../../scripts/network-error/networkError.js';

//wait is true, so notify runs synchronously.
//priority on subscribe will call the methods in priority order
//otherwise it will get invoked in the order of subscription
export const LessThanSecureEventsObservable = new Observable(
  'lessThanSecureEvent',
  true,
);

//constants specific to less than secure, for now just for fullscreen violation has counter
export const MAX_PROHIBITED_ACTION_VIOLATION = 4;

const LessSecure = () => {
  const { setStorageItem, getStorageItem } = useSessionStorage();
  const {
    updatePausedBy,
    setOpenProctorPauseDialog,
    setProctorPauseDialogProps,
    updateTestStatus,
    setOpenContinueDialog,
    setOpenSubmitDialog,
    setSubmitInProgress,
  } = useContext(AssessmentContext);
  const { isSecure, platform } = useContext(KioskContext);
  const [eventHandlers, setEventHandlers] = useState();
  const { t } = useTranslation();

  const emitEvent = useCallback((event) => {
    LessThanSecureEventsObservable.notify({ ...event });
  }, []);

  const hideDialog = useCallback(
    (appInstance) => {
      appInstance?.dialogs()?.pause.hide();
      hideCustomDialog(appInstance, () => {
        setOpenProctorPauseDialog(false);
        setOpenContinueDialog(false);
        setOpenSubmitDialog(false);
      });
    },
    [setOpenProctorPauseDialog, setOpenContinueDialog, setOpenSubmitDialog],
  );

  const emitResumeEvent = useCallback(
    (eventName) => {
      //an event needs to be emitted because there's no learnosity pause/resume event that will be emitted for pausing/resuming on testLaunched status
      let statusBeforePause = getStorageItem(
        Storage.STORAGE_STATUS_BEFORE_PAUSE,
      );
      if (statusBeforePause && statusBeforePause === TestStatus.TEST_LAUNCHED) {
        //this will handle resuming on launched(before test:start)
        emitEvent({
          eventName: eventName,
        });
      }
    },
    [getStorageItem, emitEvent],
  );

  const BACK_TO_FULLSCREEN_RESUME = useCallback(
     (appInstance, resumeNotAllowed, message) => {
      if (isFullscreen()) {
        return null;
      } else {
        if (resumeNotAllowed) {
          return [
            {
              label: t('components.lessThanSecure.backToFullscreen'),
              handle: async () => {
                await switchToFullscreen();
                setProctorPauseDialogProps(PAUSED_DUE_TO_ACTION(message, null)); //this will update the dialog to remove button
              },
            },
          ];
        } else {
          return [
            {
              label: t('components.lessThanSecure.backToFullscreenAndResume'),
              handle: async () => {
                await switchToFullscreen();
                //TA always needs readyToResume event
                emitEvent({
                  eventName: PwaEvents.READY_TO_RESUME,
                });
                setTimeout(() => {
                  hideDialog(appInstance);
                }, 50); // minor delay so testResume & readyToResume do not have the same TS
                //this is only for when status - testLaunched - user is in the intro page
                emitResumeEvent(PwaEvents.TESTER_RESUME);
              },
            },
          ];
        }
      }
    },
    [hideDialog, setProctorPauseDialogProps, emitResumeEvent, emitEvent, t],
  );

  const getFullscreenViolationMessage = useCallback(
    (counter, proctorResumeNeeded) => {
      let message = t(PROHIBITED_ACTION.NOT_FULLSCREEN.description);
      if (counter >= MAX_PROHIBITED_ACTION_VIOLATION) {
        message = message.replace(VIOLATION_COUNT_PLACEHOLDER, '');
        message = message + t(PROHIBITED_ACTION.NOT_FULLSCREEN.messageNote);
      } else if (proctorResumeNeeded) {
        message =
          t(PROHIBITED_ACTION.NOT_FULLSCREEN_PROCTOR_RESUME_ONLY.description);
      } else {
        message = message.replace(
          VIOLATION_COUNT_PLACEHOLDER,
          `${t('components.lessThanSecure.oviolationOccurrence')}: ${counter}`,
        );
      }
      return message;
    },
    [t],
  );

  const emitNecessaryEvents = useCallback(
    (
      testStatus,
      onload,
      prohibitedAction,
      previousProhibitedAction,
      action,
    ) => {
      if (testStatus === TestStatus.TEST_LAUNCHED) {
        //need to emit this event & set status because no test:pause event(usual event we send message & updateTestStatus) is emitted when pausing on testLaunched
        emitEvent({
          eventName: PwaEvents.PROHIBITED_ACTION_PAUSE,
        });
        updateTestStatus(TestStatus.TEST_PAUSED);
      }

      if (
        testStatus === TestStatus.TEST_PAUSED &&
        !onload &&
        (previousProhibitedAction !== prohibitedAction ||
          [
            PROHIBITED_ACTION.NOT_FULLSCREEN.type,
            PROHIBITED_ACTION.OUT_OF_FOCUS.type,
          ].includes(action.type))
      ) {
        //need to emit this new event, so that a pause message will be sent. without this, there's no new pause event(hence no message) since it's already in pause state
        //only send it if pause event is not equal to the previous prohibited action
        //moved checking here of same prohibited action. updating of dialog is still needed when exiting fullscreen
        emitEvent({
          eventName: PwaEvents.PROHIBITED_ACTION_PAUSE,
        });
      }
    },
    [emitEvent, updateTestStatus],
  );

  const showProhibitedActionDialog = useCallback(
    (config, appInstance, action, onload) => {
      let buttons = null;
      let counter = null;
      let priorityAction = getPriorityAction(action);
      const prohibitedAction = `${PROHIBITED_ACTION.PREFIX}${action.type}`;

      if (action === PROHIBITED_ACTION.NOT_FULLSCREEN) {
        counter = incrementViolationCount();
      }

      let proctorResumeNeeded = getStorageItem(
        Storage.STORAGE_PROCTOR_RESUME_NEEDED,
      );

      //get message for the dialog
      let message;
      if (priorityAction === PROHIBITED_ACTION.NOT_FULLSCREEN) {
        message = getFullscreenViolationMessage(counter, proctorResumeNeeded);
      } else {
        message = t(priorityAction.description);
      }

      let resumeNotAllowed =
        priorityAction !== PROHIBITED_ACTION.NOT_FULLSCREEN ||
        counter >= MAX_PROHIBITED_ACTION_VIOLATION ||
        proctorResumeNeeded;
      if (!proctorResumeNeeded && resumeNotAllowed) {
        setStorageItem(Storage.STORAGE_PROCTOR_RESUME_NEEDED, true); // this variable is needed by the readyToResume event - esp with back to fullscreen(case: back to fullscreen and resume - no need to send readyToResume)
      }

      //Determine if button is needed : start
      if (!config.allowNonFullscreen) {
        buttons = BACK_TO_FULLSCREEN_RESUME(
          appInstance,
          resumeNotAllowed,
          message,
        );
      }
      //Determine if button is needed : end

      const previousProhibitedAction = getStorageItem(
        Storage.STORAGE_PROHIBITED_ACTION,
      );

      let testStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
      if (testStatus !== TestStatus.TEST_PAUSED) {
        setStorageItem(Storage.STORAGE_STATUS_BEFORE_PAUSE, testStatus);
      }

      //set storage
      updatePausedBy(PwaEvents.ACTOR_SYSTEM);
      setStorageItem(
        Storage.STORAGE_PAUSED_REASON,
        `${PROHIBITED_ACTION.PREFIX}${priorityAction.type}`,
      );
      setStorageItem(Storage.STORAGE_PROHIBITED_ACTION, prohibitedAction);
      //emit necessary events to psychometric handler

      emitNecessaryEvents(
        testStatus,
        onload,
        prohibitedAction,
        previousProhibitedAction,
        action,
      );

      //show the dialog : start
      let statusBeforePause = getStorageItem(
        Storage.STORAGE_STATUS_BEFORE_PAUSE,
      );
      if (
        testStatus === TestStatus.TEST_INSESSION ||
        (testStatus === TestStatus.TEST_PAUSED &&
          statusBeforePause === TestStatus.TEST_INSESSION)
      ) {
        showCustomDialog(
          appInstance,
          PAUSED_DUE_TO_ACTION(message),
          testStatus === TestStatus.TEST_PAUSED && !onload,
        );
      }
      //Set submit In progress to undefined to make sure
      //Learnosity error save dialog does not show up in case the
      //user already submitted
      setSubmitInProgress();
      //If there is a prohibited dialog, lets make sure continue dialog is set
      //to false so that prohibited dialog shows on top.
      setOpenContinueDialog(false);
      setProctorPauseDialogProps(PAUSED_DUE_TO_ACTION(message, buttons));
      setOpenProctorPauseDialog(true);
      //show the dialog : end
    },
    [
      updatePausedBy,
      setOpenProctorPauseDialog,
      setProctorPauseDialogProps,
      getStorageItem,
      BACK_TO_FULLSCREEN_RESUME,
      setStorageItem,
      getFullscreenViolationMessage,
      emitNecessaryEvents,
      setOpenContinueDialog,
      setSubmitInProgress,
      t
    ],
  );

  //This handles showing of prohibited action dialog when it's already paused on load - refresh scenario
  const pausedOnLoad = useCallback(
    (config, appInstance) => {
      let pausedReason = getStorageItem(Storage.STORAGE_PAUSED_REASON);
      if (pausedReason?.startsWith(PROHIBITED_ACTION.PREFIX)) {
        let actionKey = pausedReason.replace(PROHIBITED_ACTION.PREFIX, '');
        showProhibitedActionDialog(
          config,
          appInstance,
          PROHIBITED_ACTION[actionKey],
          true,
        );
      }
    },
    [showProhibitedActionDialog, getStorageItem],
  );

  const enableLessThanSecure = useCallback(() => {
    let pwaConfig = getPwaConfig(Storage.STORAGE_PWA_CONFIG);
    if (!isSecure && !pwaConfig?.disableLockdown) {
      return true;
    }
    return false;
  }, [isSecure]);

  //note this handler will only be subscribed for less than secure case
  const learnosityEventHandler = useCallback(
    async ({ appInstance, eventName }) => {
      if (appInstance) {
        let config = getConfigBasedOnPnp() || {};
        let testStatus;
        switch (eventName) {
          case LEARNOSITY_EVENTS.TEST_READY:
            // it needs to be in test start because showing the dialog on test ready will not clear the pausedReason on resume because it's not really paused in learnosity's perspective
            let registeredEventHandlers = enforceLessSecure(
              config,
              (action) =>
                showProhibitedActionDialog(config, appInstance, action),
              platform,
              emitEvent,
            );
            setEventHandlers(registeredEventHandlers);
            testStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
            if (testStatus === TestStatus.TEST_PAUSED) {
              //we know this is a refresh scenario
              setFullscreen(isApiFullscreen()); //this is for refresh - makes sure fullscreen is up to date
              let statusBeforePause = getStorageItem(
                Storage.STORAGE_STATUS_BEFORE_PAUSE,
              );
              if (statusBeforePause === TestStatus.TEST_LAUNCHED) {
                pausedOnLoad(config, appInstance);
              }
            } else {
              //normal scenario - we check our usual
              if (!config.allowNonFullscreen && !isApiFullscreen()) {
                setFullscreen(false);
                showProhibitedActionDialog(
                  config,
                  appInstance,
                  PROHIBITED_ACTION.NOT_FULLSCREEN,
                );
              }
              if (!config.allowSplitScreen && isScreenSplit) {
                showProhibitedActionDialog(
                  config,
                  appInstance,
                  PROHIBITED_ACTION.SPLIT_SCREEN,
                );
              }
            }

            break;
          case LEARNOSITY_EVENTS.ITEM_LOAD:
            testStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
            if (testStatus === TestStatus.TEST_PAUSED) {
              //we know this is a refresh scenario
              pausedOnLoad(config, appInstance);
            }
            break;
          case PwaEvents.TESTER_SAVEANDEXIT: //This event is generated by NMC custom Save/Exit button (success save)
          case LEARNOSITY_EVENTS.TEST_FINISHED_SAVE: //This event is generated by Learnosity Save/Exit button
          case LEARNOSITY_EVENTS.TEST_SUBMIT_SUCCESS: //This event is generated by Learnosity Submit button, this is also emiited after receiving proctor submit
            if (enableLessThanSecure() && eventHandlers) {
              removeEventListeners(eventHandlers);
            }
            break;
          default:
            break;
        }
      }
    },
    [
      showProhibitedActionDialog,
      getStorageItem,
      platform,
      enableLessThanSecure,
      eventHandlers,
      emitEvent,
      pausedOnLoad,
    ],
  );

  //note this handler will only be subscribed for less than secure case
  const pwaEventHandler = useCallback(
    async ({ appInstance, eventName }) => {
      let config = getConfigBasedOnPnp() || {};
      switch (eventName) {
        case PwaEvents.PROCTOR_RESUME_CHECK:
          if (appInstance) {
            //two things to check on resume - it's still in fullscreen and it's in focus
            //Also need to check -- If there is no split screen and no network errors
            if (
              (config.allowNonFullscreen || isFullscreen()) &&
              (config.enableOutOfFocus || document.hasFocus()) &&
              (config.allowSplitScreen || !isScreenSplit) &&
              !hasNetworkErrors()
            ) {
              hideDialog(appInstance);
              emitResumeEvent(PwaEvents.PROCTOR_RESUME);
            }
          }
          break;
        case PwaEvents.PROCTOR_SAVEANDEXIT: //This event is generated by Proctor Save/Exit button
          if (enableLessThanSecure() && eventHandlers) {
            removeEventListeners(eventHandlers);
          }
          break;
        default:
          break;
      }
    },
    [hideDialog, enableLessThanSecure, eventHandlers, emitResumeEvent],
  );

  useEffect(() => {
    //only enforce less secure if !isSecure
    if (enableLessThanSecure()) {
      LearnosityContainerObservable.subscribe(
        learnosityEventHandler,
        'lessSecureHandler',
      );
      PWAEventsObservable.subscribe(pwaEventHandler, 'lessSecureHandler');
    }
    return () => {
      if (enableLessThanSecure()) {
        LearnosityContainerObservable.unsubscribe('lessSecureHandler');
        PWAEventsObservable.unsubscribe('lessSecureHandler');
      }
    };
  }, [learnosityEventHandler, pwaEventHandler, enableLessThanSecure]);

  useEffect(() => {
    //This is for cleaning up when mounting. Prevent multiple event handlers.
    return () => {
      if (eventHandlers && enableLessThanSecure()) {
        removeEventListeners(eventHandlers);
      }
    };
  }, [eventHandlers, enableLessThanSecure]);

  return;
};

export default LessSecure;
